/**
 * Dial input component
 * @author Carlos Lopez Rodriguez <clopez@adasasistemas.com>
 */

// Library
import React, { useId, useMemo, useState } from 'react';
import { OtpInput, Text, TextFieldQldDS } from '@components';
// Components
import { WarningQld } from '@public/icons';
import {
	calculateMeterDialDigitsNumber,
	isAnalogMeterDial,
	variableDialMaxLength,
	variableDialPattern,
} from '@utils';

// CSS Style for this module
import styles from './dialInputQldDS.module.scss';

/**
 * Interface for DialInput props
 */
interface DialInputQldDSProps {
	/**
	 * The value of the dial input as a concatenation of each
	 * character from every individual input value
	 */
	value?: string;
	/**
	 * Hint text to be displayed
	 */
	hintText?: string;
	/**
	 * onChange function for keeping track field value
	 * debounce is built in
	 */
	handleChange?: (value: string) => void;
	/**
	 * Dial type with patern 'm' followed by 2 digits (e.g. 'm34') to determine the decimal digits
	 */
	dialType?: string | null;
	/**
	 * Msg to be displayed when the status is error
	 */
	errorMsg?: string;
	/**
	 * Boolean for determining whether this field has to be autofocused
	 */
	autoFocus?: boolean;
	/**
	 * Function to be called when the input loses focus
	 */
	onBlur?: () => void;
}

/**
 * DialInput component is an extension of an otp input which lets users enter digits
 * individually in a set of inputs stylized as a meter's dial
 * @param {DialInputQldDSProps} Prop
 * @returns a meter's dial input
 */
export const DialInputQldDS = ({
	value,
	hintText,
	dialType,
	errorMsg,
	autoFocus,
	handleChange = () => {
		return;
	},
	onBlur,
}: DialInputQldDSProps) => {
	const internalId = useId();
	const [hintId] = useState(`${internalId}-hint`);
	const [errorId] = useState(`${internalId}-error`);
	const isAnalogDial = useMemo(() => isAnalogMeterDial(dialType), [dialType]);
	const meterDialDigitsNumber = useMemo(() => calculateMeterDialDigitsNumber(dialType), [dialType]);
	const numberOfDigitsAfter = useMemo(
		() => (isAnalogDial ? Number(dialType?.charAt(2)) : 0),
		[isAnalogDial, dialType],
	);
	const hasError = useMemo(() => Boolean(errorMsg), [errorMsg]);

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		let value = e.target.value.replace(/[^0-9.]/g, '');

		// Add leading zero if value starts with decimal
		if (value.startsWith('.')) {
			value = '0' + value;
		}

		// Handle multiple decimal points
		const parts = value.split('.');
		if (parts.length > 2) {
			value = parts[0] + '.' + parts.slice(1).join('');
		}

		// Enforce max length
		if (value.includes('.')) {
			const [before, after] = value.split('.');
			if (before.length > 11) {
				value = before.slice(0, 11) + '.' + after;
			}
			if (value.length > 13) {
				value = value.slice(0, 13);
			}
		} else if (value.length > 12) {
			value = value.slice(0, 12);
		}

		handleChange(value);
	};

	const handleBlur = () => {
		if (value?.endsWith('.')) {
			handleChange(value.slice(0, -1));
		}
		onBlur?.();
	};

	return isAnalogDial ? (
		<div className={styles.container}>
			<div className={styles.textContainer}>
				{hintText && (
					<div>
						<Text variant="muted" id={hintId}>
							{hintText}
						</Text>
					</div>
				)}
				{hasError && (
					<div className={styles.errorContainer}>
						<Text variant="error" id={errorId} role="alert" className={styles.errorContent}>
							<WarningQld customStyles={styles.errorIcon} /> {errorMsg}
						</Text>
					</div>
				)}
			</div>
			<OtpInput
				containerStyles={styles['dial-container']}
				inputStyles={styles.digit}
				specialInputStyles={styles['digit--decimal']}
				value={value}
				valueLength={meterDialDigitsNumber}
				specialInputsNumber={numberOfDigitsAfter}
				specialInputsSide="right"
				placeholder="0"
				onChange={handleChange}
				errorMsg=""
				status={hasError ? 'error' : 'valid'}
				autoFocus={autoFocus}
				onBlur={onBlur}
			/>
		</div>
	) : (
		<TextFieldQldDS
			size="large"
			maxLength={variableDialMaxLength}
			customStyles={styles['variable-dial-input']}
			type="text"
			inputMode="decimal"
			handleChange={handleInputChange}
			defaultValue={value}
			pattern={variableDialPattern}
			errorMsg={errorMsg}
			autoFocus={autoFocus}
			hintText={hintText}
			onBlur={handleBlur}
		/>
	);
};
